// src/service/MessagingService.js
//
// Unified guest inbox: every guest conversation (email + OTA chat via
// Channex, more channels later) in one screen. See the Messages tab
// build task for the full API contract — this is a thin wrapper, no
// local caching (the inbox needs to stay live, unlike reservation
// lists). Singleton, patterned after OtaMessageService.js.
import apiClient from './ApiClient';

class MessagingService {
  /** { unreadTotal:int, awaitingReply:int, enabled:bool } */
  async getSummary() {
    return apiClient.get('/v1/messaging/summary');
  }

  /**
   * @param {Object} [params]
   * @param {'all'|'unread'|'needs_reply'} [params.filter]
   * @param {string} [params.q] guest name / confirmation number / email
   * @param {number} [params.limit]
   * @param {string} [params.before] ISO timestamp of the last row's lastMessageAt — pagination cursor
   * @returns {Promise<{conversations:Array, unreadTotal:number, awaitingReply:number, hasMore:boolean}>}
   */
  async listConversations({ filter, q, limit = 50, before } = {}) {
    const params = new URLSearchParams();
    if (filter) params.set('filter', filter);
    if (q) params.set('q', q);
    params.set('limit', String(limit));
    if (before) params.set('before', before);
    return apiClient.get(`/v1/messaging/conversations?${params.toString()}`);
  }

  /** Find-or-create the conversation for a reservation. */
  async createConversation(reservationId) {
    return apiClient.post('/v1/messaging/conversations', { reservationId });
  }

  /** { conversation, messages, capabilities } */
  async getConversation(conversationId) {
    return apiClient.get(`/v1/messaging/conversations/${conversationId}`);
  }

  /**
   * @param {number} conversationId
   * @param {{channel: 'email'|'ota', text: string, subject?: string}} message
   */
  async sendMessage(conversationId, { channel, text, subject }) {
    const payload = { channel, text };
    if (subject) payload.subject = subject;
    return apiClient.post(`/v1/messaging/conversations/${conversationId}/messages`, payload);
  }

  async markRead(conversationId) {
    return apiClient.post(`/v1/messaging/conversations/${conversationId}/read`, {});
  }

  /** action: 'close' | 'no-reply-needed' */
  async threadAction(conversationId, action) {
    return apiClient.post(`/v1/messaging/conversations/${conversationId}/thread-action`, { action });
  }
}

const messagingService = new MessagingService();
export default messagingService;
